<template>
  <Dialog
    :header="studentActivity?.student?.name ?? 'Atividade Aluno'"
    :visible="show"
    @update:visible="$emit('update:show', $event)"
    modal
    :style="{ width: '60vw', minWidth: '300px', maxWidth: '800px' }"
  >
    <Loader :loader="loading?.activity" />
    <div id="card-activity" class="card-activity" v-if="!loading?.activity">
      <header>
        <h4>{{ `Atividade Enviada - ${studentActivity?.title}` }}</h4>
      </header>
      <div>
        <strong>{{ "Comentário: " }}</strong>
        {{ studentActivity?.textAnswer ?? "Nenhum comentário adicionado" }}
      </div>
      <div class="flex flex-row items-center gap-2">
        <strong>
          {{ "Anexo enviado: " }}
        </strong>
        <div
          v-for="file in studentActivity?.mediaAnswer"
          v-if="studentActivity?.mediaAnswer?.length > 0"
          :key="file.id"
        >
          <div
            class="flex flex-row justify-between items-center bg-h-primary-50 pr-4 cursor-pointer"
            @click="downloadActivity(file)"
          >
            <Button
              icon="pi pi-file"
              class="btn-simple"
              :label="file.name + '.' + file.mimetype"
            />
            <v-icon name="hi-solid-download" />
          </div>
        </div>
        <p v-else>{{ "Nenhum arquivo anexado" }}</p>
      </div>
    </div>
    <div class="" v-if="studentActivity?.status.value === 'FEEDBACK'">
      <h4 class="py-4">{{ "Feedbacks" }}</h4>
      <div class="feedback-card">
        <span>
          <strong>{{ "Nota: " }}</strong>
          {{ studentActivity?.score ?? "Nenhuma nota atribuída" }}
        </span>
        <span>
          <strong>{{ "Comentários do Professor:" }}</strong>
          {{ studentActivity?.feedback ?? "Nenhum feedback adicionado" }}
        </span>
      </div>
    </div>
    <form
      @submit.prevent="submitCorrection"
      v-else-if="studentActivity?.status.value === 'ASSIGNED'"
    >
      <div class="flex flex-col gap-4 py-4">
        <label for="input-score">{{
          `Pontuação 0 - ${studentActivity?.scoreRange} `
        }}</label>
        <InputNumber
          :placeholder="
            $t('labels.generic', { pt: 'Insira uma nota', en: 'Insert grade' })
          "
          v-model="form.score"
          id="input-score"
          mode="decimal"
          :min="0"
          :max="studentActivity?.scoreRange"
          :class="[{ 'p-invalid': v$?.score?.$error }]"
          v-tooltip.focus.top="
            'Nota e feedback devem ser enviados após o prazo final da atividade'
          "
        />
        <Errors :errors="v$?.score?.$errors" />
      </div>
      <div class="flex flex-col gap-4">
        <label for="input-feedback">{{ "Descrição da Atividade" }}</label>
        <textarea
          :placeholder="
            $t('labels.generic', {
              pt: 'Adicione um Feedback',
              en: 'Add a Feedback',
            })
          "
          rows="5"
          cols="20"
          v-model="form.feedback"
          class="border rounded-md p-3 p-inputtext"
          :class="[{ 'p-invalid': v$?.feedback?.$error }]"
          v-tooltip.focus.top="
            'Nota e feedback devem ser enviados após o prazo final da atividade'
          "
          :style="{ resize: 'none' }"
        />
        <Errors :errors="v$?.feedback?.$errors" />
        <div class="flex flex-row gap-2 items-center mb-4">
          <i class="pi pi-info-circle" />
          <p>
            {{ "Após a correção, a nota e feedback não poderão ser editados" }}
          </p>
        </div>
      </div>
      <span class="flex justify-end gap-2 mt-4">
        <Button
          @click="cancelCorrection"
          :label="$t('labels.generic', { en: 'Cancel', pt: 'Cancelar' })"
          class="btn-outline"
        />
        <Button
          type="submit"
          :label="$t('labels.generic', { en: 'Correct', pt: 'Corrigir' })"
          :loading="loading?.submit"
          class="btn-primary"
        />
      </span>
    </form>
  </Dialog>
</template>
<script lang="ts">
import useVuelidate from "@vuelidate/core";
import { defineComponent } from "vue";
import { validator } from "./correction.validations";
import { FormCorrection } from "@/models/activity.model";
import { ActivityRest } from "@/services/rest/activity.rest";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { MediasRest } from "@/services/rest/media.rest";
import { GenericsUtils } from "@/utils/generics.util";

export default defineComponent({
  props: ["studentActivity", "show"],
  emits: ["update:show", "studentActivityUpdated"],
  data() {
    return {
      loading: { submit: false, activity: false },
      form: new FormCorrection(),
    };
  },
  computed: {
    rest(): ActivityRest {
      return new ActivityRest();
    },
    mediaRest(): MediasRest {
      return new MediasRest();
    },
    utils(): GenericsUtils {
      return new GenericsUtils();
    },
    validations() {
      return validator(this.studentActivity);
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  methods: {
    cancelCorrection() {
      this.$emit("update:show", false);
      this.resetForm();
    },
    submitCorrection() {
      this.v$.$validate();
      if (this.v$.$invalid) return;
      this.sendCorrection();
    },
    sendCorrection() {
      this.loading.submit = true;

      this.rest
        .sendFeedback(this.studentActivity.studentActivityId, this.form)
        .subscribe({
          next: (response) => {
            this.loading.submit = false;
            this.$emit("studentActivityUpdated");
            this.$emit("update:show", false);
            this.resetForm();
            this.$toast.add(ToastFactory.success(Messages.FEEDBACK_SUCCESS));
          },
          error: () => {
            this.loading.submit = false;
          },
          complete: () => {
            this.loading.submit = false;
          },
        });
    },
    downloadActivity(file: any) {
      this.mediaRest.getMedia(file?.id!).subscribe({
        next: async (response) => {
          const mediaUrl = this.utils.getSecureUrl(response.url);
          try {
            const fetchResponse = await fetch(mediaUrl);
            const blob = await fetchResponse.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = response.name || "download";
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          } catch (error) {
            console.error("Erro ao fazer download:", error);
          }
        },
      });
    },
    resetForm() {
      this.form = new FormCorrection();
    },
    getStudentActivity() {
      if (!this.studentActivity.studentActivityId) return;
      this.rest
        .getStudentActivitiesById(this.studentActivity.studentActivityId)
        .subscribe({
          next: (response) => {
            this.studentActivity.textAnswer = response?.textAnswer;
            this.studentActivity.mediaAnswer = response?.mediaAnswer;
            this.studentActivity.overdue = response?.overdue;
            this.studentActivity.score = response.score;
            this.studentActivity.feedback = response.feedback;
          },
          error: (err) => {
            console.error("error", err);
            this.$emit("update:show", false);
            this.loading.activity = false;
          },
          complete: () => {
            this.loading.activity = false;
          },
        });
    },
  },
  watch: {
    show(newValue: boolean) {
      if (newValue) {
        this.getStudentActivity();
      }
    },
  },
});
</script>
<style scoped lang="scss">
.card-activity {
  @apply border-b-4 border border-b-h-primary-500 rounded-md p-4 flex flex-col gap-4 mb-4;
}
.feedback-card {
  @apply border-b-4 border border-b-h-primary-500 rounded-md p-4 flex flex-col gap-4 mb-4;
}
</style>
